import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What is the best Zendesk alternative?',
    a: 'The best Zendesk alternative depends on budget and focus. For pricing without seat fees and AI ticket structuring included, Kora (pay-as-you-go credits: $1 = 10 credits, 1 credit per ticket, 1,100 free on signup) is the strongest option. For email-first simplicity, Help Scout. For the Zoho ecosystem, Zoho Desk. For open source, FreeScout. Teams usually leave Zendesk over per-agent costs — Suite Team is $55/agent/month and Suite Professional is $115/agent/month — and AI sold as a paid add-on.',
  },
  {
    q: 'Why do teams switch from Zendesk?',
    a: 'The recurring reasons: per-agent pricing (Suite Team at $55/agent/month means a 10-agent team pays $550/month; Professional is $115/agent/month), AI add-on fees on top of seat costs, and implementation overhead — Zendesk deployments often need a dedicated administrator. Smaller teams that want a working help desk in an afternoon switch to lighter tools.',
  },
  {
    q: 'Is there a free Zendesk alternative?',
    a: 'Kora has no seat fees and unlimited members: new workspaces get 1,100 free credits with no card, then a ticket costs $0.10 (1 credit). AI ticket structuring is free. FreeScout is a self-hosted open-source alternative. Help Scout is free for 50 contacts.',
  },
];

// Verified June 2026 from Zendesk's official pricing page; re-verified quarterly.
const COST_TABLE = [
  { agents: 5, zendesk: 275, kora: 30 },
  { agents: 10, zendesk: 550, kora: 30 },
  { agents: 25, zendesk: 1375, kora: 30 },
];

export function ZendeskAlternativePage() {
  useEffect(() => {
    const title = 'Zendesk Alternative — Flat Pricing, AI Included | Kora';
    const description =
      'Looking for a Zendesk alternative? Kora has no seat fees and unlimited members, charges $0.10 per ticket and starts with 1,100 free credits.';
    setDocumentMeta(title, description);
    setCanonical('/zendesk-alternative');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/zendesk-alternative', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Zendesk Alternative', url: 'https://usekora.app/zendesk-alternative' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Alternative · Updated September 2026"
      title="Looking for a Zendesk alternative?"
      subtitle="Kora is what teams switch to when per-agent pricing and AI add-ons stop making sense."
      ctaTitle="Switch from Zendesk — start free"
      ctaSubtitle="Pay as you go — $1 = 10 credits, 1 credit per ticket. Start with 1,100 free credits. Import via API."
      answer={
        <p>
          Kora is a Zendesk alternative built for teams that want AI included and pricing with no
          seat fees. Zendesk charges per agent — Suite Team is $55/agent/month and Suite
          Professional is $115/agent/month, so a 10-agent team pays $550–$1,150/month before AI
          add-ons. Kora charges <strong>per ticket with credits — $1 = 10 credits, no seat fees</strong>, with AI
          ticket structuring free, an AI Analyst at 1 credit per answer — and it starts with{' '}
          <strong>1,100 free credits</strong> and unlimited members. Paste a Slack thread,
          WhatsApp forward, or customer email and Kora's AI turns it into a structured,
          prioritized ticket in seconds.
        </p>
      }
    >
      <ContentSection title="Why teams switch from Zendesk">
        <p>
          Zendesk is the most capable enterprise help desk — the switch reasons are cost and
          weight, not features:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Per-agent costs compound.</strong> Suite Team at $55/agent/month means every
            hire multiplies the bill; Professional at $115/agent/month compounds faster.
          </li>
          <li>
            <strong>AI is an add-on.</strong> Zendesk sells its AI agents as paid additions on
            top of seat pricing, while Kora includes AI ticket structuring with no extra fee.
          </li>
          <li>
            <strong>Implementation overhead.</strong> Zendesk deployments typically need an
            administrator and weeks of configuration. Smaller teams want a working help desk in
            an afternoon.
          </li>
        </ul>
      </ContentSection>

      <ContentSection title="Cost comparison at team size">
        <p>
          Monthly cost comparison — Zendesk Suite Team ($55/agent/month, checked 2026-10-19)
          versus Kora (pay-as-you-go credits — $1 = 10 credits, $0.10 per ticket; the Kora column assumes 300 tickets a month and does not change with team size):
        </p>
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Team size</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Zendesk Suite Team</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Kora</th>
              </tr>
            </thead>
            <tbody>
              {COST_TABLE.map((row) => (
                <tr key={row.agents} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-zinc-900 ">{row.agents} agents</td>
                  <td className="px-4 py-3 text-zinc-500 ">${row.zendesk.toLocaleString()}/mo</td>
                  <td className="px-4 py-3 font-medium text-green-600 ">${row.kora}/mo</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-zinc-400 ">
          Zendesk pricing checked 2026-10-19 on the official pricing page; re-verified
          quarterly. Kora has no seat fees; members are unlimited and the cost depends only on tickets created.
        </p>
      </ContentSection>

      <ContentSection title="What you keep">
        <p>
          The core of a Zendesk workflow: omnichannel intake, categories, priorities, SLAs, a
          public API (20+ endpoints; 1 credit per 400 responses), knowledge base,
          and customer-facing ticket views — including{' '}
          <Link to="/docs" className="underline hover:text-zinc-900 ">Embedded Sessions</Link>{' '}
          for sharing live ticket views with external stakeholders without a login.
        </p>
      </ContentSection>

      <ContentSection title="The detailed comparison">
        <p>
          For a feature-by-feature breakdown including the full pricing matrix and where each
          tool wins, see the{' '}
          <Link to="/compare/kora-vs-zendesk" className="underline hover:text-zinc-900 font-medium">
            Kora vs Zendesk
          </Link>{' '}
          comparison. Other teams in this situation also compare{' '}
          <Link to="/compare/kora-vs-freshdesk" className="underline hover:text-zinc-900 ">Freshdesk</Link>,{' '}
          <Link to="/compare/kora-vs-intercom" className="underline hover:text-zinc-900 ">Intercom</Link>, and{' '}
          <Link to="/compare/kora-vs-help-scout" className="underline hover:text-zinc-900 ">Help Scout</Link>.
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions teams ask when leaving Zendesk." />
    </ContentPage>
  );
}
